import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import Navbar from '../components/Navbar';
import {
    Bot, Play, Square, TrendingUp, TrendingDown, AlertTriangle,
    Clock, DollarSign, Target, CheckCircle, XCircle, RefreshCw,
    ChevronRight, Package, BarChart3, History, Settings,
    ArrowUpCircle, ArrowDownCircle, Wallet, Zap, Activity,
    Sparkles, Shield, Cpu, LineChart, ArrowRight
} from 'lucide-react';

const FuturesBotTradingPage = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [packages, setPackages] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [cryptos, setCryptos] = useState([]);
    const [strategyPerformance, setStrategyPerformance] = useState([]);
    const [trades, setTrades] = useState([]);
    const [reports, setReports] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [settings, setSettings] = useState({});
    const [activeTab, setActiveTab] = useState('overview');
    const [apiError, setApiError] = useState(null);
    const [walletBalance, setWalletBalance] = useState(0);

    // Subscription form
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [investmentAmount, setInvestmentAmount] = useState(100);
    const [selectedCryptos, setSelectedCryptos] = useState(['BTCUSDT', 'ETHUSDT']);
    const [leverage, setLeverage] = useState(2);
    const [positionMode, setPositionMode] = useState('BOTH');
    const [subscribing, setSubscribing] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setApiError(null);
            const [packagesRes, subscriptionRes, cryptosRes, walletRes] = await Promise.all([
                api.get('/futures-bot/packages'),
                api.get('/futures-bot/subscription'),
                api.get('/futures-bot/top-cryptos'),
                api.get('/wallet/balances')
            ]);

            // Get USDT available balance (total - locked)
            const usdtBalance = walletRes.data.balances?.find(b => b.symbol === 'USDT');
            const totalBalance = parseFloat(usdtBalance?.balance || 0);
            const lockedBalance = parseFloat(usdtBalance?.lockedBalance || 0);
            setWalletBalance(totalBalance - lockedBalance);

            setPackages(packagesRes.data.packages || []);
            setSettings(packagesRes.data.settings || {});
            setSubscription(subscriptionRes.data.subscription);
            setCryptos(cryptosRes.data.cryptos || []);
            setStrategyPerformance(cryptosRes.data.strategyPerformance || []);

            if (subscriptionRes.data.subscription) {
                setSelectedCryptos(subscriptionRes.data.subscription.selectedCryptos || []);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setApiError(error.response?.data?.message || 'Failed to load data. Please ensure server is running.');
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const refreshPnL = async () => {
        if (subscription) {
            try {
                const res = await api.get('/futures-bot/subscription');
                setSubscription(res.data.subscription);
            } catch (error) {
                console.error('Error refreshing PnL:', error);
            }
        }
    };

    useEffect(() => {
        if (subscription?.isBotRunning) {
            const interval = setInterval(refreshPnL, 30000);
            return () => clearInterval(interval);
        }
    }, [subscription?.isBotRunning]);

    const fetchTrades = async () => {
        try {
            const res = await api.get('/futures-bot/trades');
            setTrades(res.data.trades || []);
        } catch (error) {
            console.error('Error fetching trades:', error);
        }
    };

    const fetchReports = async () => {
        try {
            const res = await api.get('/futures-bot/reports?days=30');
            setReports(res.data.reports || []);
        } catch (error) {
            console.error('Error fetching reports:', error);
        }
    };

    const fetchTransactions = async () => {
        try {
            const res = await api.get('/futures-bot/transactions');
            setTransactions(res.data.transactions || []);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        }
    };

    useEffect(() => {
        if (activeTab === 'trades') fetchTrades();
        if (activeTab === 'reports') fetchReports();
        if (activeTab === 'transactions') fetchTransactions();
    }, [activeTab]);

    const handleSubscribe = async () => {
        if (!selectedPackage) {
            toast.error('Please select a package');
            return;
        }
        if (selectedCryptos.length === 0) {
            toast.error('Please select at least one crypto');
            return;
        }
        setSubscribing(true);
        try {
            await api.post('/futures-bot/subscribe', {
                packageId: selectedPackage.id,
                investmentAmount,
                selectedCryptos,
                leverage,
                positionMode
            });
            toast.success('Futures Bot subscription activated!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Subscription failed');
        } finally {
            setSubscribing(false);
        }
    };

    const handleStartBot = async () => {
        try {
            await api.post('/futures-bot/start');
            toast.success('Futures Bot started!');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to start bot');
        }
    };

    const handleStopBot = async () => {
        try {
            await api.post('/futures-bot/stop');
            toast.success('Futures Bot stopped');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to stop bot');
        }
    };

    const handleUpdateCryptos = async () => {
        try {
            await api.put('/futures-bot/cryptos', { selectedCryptos });
            toast.success('Cryptos updated');
        } catch (error) {
            toast.error('Failed to update cryptos');
        }
    };

    const handleUpdateLeverage = async (newLeverage) => {
        try {
            const res = await api.put('/futures-bot/leverage', { leverage: newLeverage });
            setLeverage(res.data.leverage);
            toast.success(`Leverage updated to ${res.data.leverage}x`);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update leverage');
        }
    };

    const toggleCrypto = (symbol) => {
        setSelectedCryptos(prev =>
            prev.includes(symbol)
                ? prev.filter(s => s !== symbol)
                : [...prev, symbol].slice(0, 10)
        );
    };

    // Strategy data with professional icons
    const strategies = [
        { name: 'Triple EMA', icon: TrendingUp, gradient: 'from-blue-600 to-blue-400', rate: '65-70%', desc: 'Trend Following', shadow: 'shadow-blue-500/20' },
        { name: 'RSI MACD', icon: BarChart3, gradient: 'from-emerald-600 to-emerald-400', rate: '60-65%', desc: 'Momentum', shadow: 'shadow-emerald-500/20' },
        { name: 'Bollinger', icon: Target, gradient: 'from-violet-600 to-violet-400', rate: '55-60%', desc: 'Band Bounce', shadow: 'shadow-violet-500/20' },
        { name: 'VWAP Volume', icon: Activity, gradient: 'from-amber-600 to-amber-400', rate: '70-75%', desc: 'Volume Based', shadow: 'shadow-amber-500/20' },
        { name: 'Multi TF', icon: LineChart, gradient: 'from-cyan-600 to-cyan-400', rate: '75-80%', desc: 'Multi-Timeframe', shadow: 'shadow-cyan-500/20' }
    ];

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#0a0d10] via-[#0d1117] to-[#0f1419]">
                <Navbar />
                <div className="flex items-center justify-center h-96">
                    <div className="text-center">
                        <div className="relative w-16 h-16 mx-auto mb-4">
                            <div className="absolute inset-0 rounded-full border-4 border-yellow-500/20"></div>
                            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-yellow-500 animate-spin"></div>
                            <Zap className="absolute inset-0 m-auto text-yellow-500" size={24} />
                        </div>
                        <p className="text-gray-400">Loading Futures Bot...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#0a0d10] via-[#0d1117] to-[#0f1419]">
            <Navbar />

            {/* Hero Section */}
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 via-transparent to-orange-500/5"></div>
                <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl"></div>

                <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-12">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 mb-4">
                            <Sparkles className="text-yellow-500" size={16} />
                            <span className="text-sm text-yellow-400 font-medium">AI-Powered Trading</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-white via-gray-100 to-gray-300 bg-clip-text text-transparent mb-4">
                            Futures Bot Trading
                        </h1>
                        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                            Intelligent futures trading with 5 professional strategies • Long & Short positions
                        </p>
                    </div>

                    {/* Strategy Cards - Premium Design */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4 mb-8">
                        {strategies.map((strat, i) => {
                            const IconComponent = strat.icon;
                            return (
                                <div
                                    key={strat.name}
                                    className={`group relative overflow-hidden rounded-2xl bg-gradient-to-br ${strat.gradient} p-[1px] transition-all duration-300 hover:scale-105 hover:shadow-xl ${strat.shadow}`}
                                >
                                    <div className="relative h-full rounded-2xl bg-[#0d1117] p-4 sm:p-5">
                                        <div className={`w-12 h-12 sm:w-14 sm:h-14 mb-3 rounded-xl bg-gradient-to-br ${strat.gradient} flex items-center justify-center transform group-hover:scale-110 transition-transform`}>
                                            <IconComponent className="text-white" size={24} strokeWidth={2.5} />
                                        </div>
                                        <div className="font-bold text-white text-sm sm:text-base mb-1">{strat.name}</div>
                                        <div className="text-xs text-gray-500 mb-2">{strat.desc}</div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                                            <span className="text-xs font-semibold text-green-400">{strat.rate}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 pb-12">
                {!subscription ? (
                    <div className="space-y-8">
                        {/* Features Bar */}
                        <div className="flex flex-wrap justify-center gap-4 sm:gap-8 py-4">
                            {[
                                { icon: Shield, text: 'Max 3x Leverage' },
                                { icon: Cpu, text: 'Auto Strategy Selection' },
                                { icon: LineChart, text: 'Long & Short Positions' }
                            ].map((feat, i) => (
                                <div key={i} className="flex items-center gap-2 text-gray-400">
                                    <feat.icon size={18} className="text-yellow-500" />
                                    <span className="text-sm font-medium">{feat.text}</span>
                                </div>
                            ))}
                        </div>

                        {/* Packages Section */}
                        <div>
                            <div className="flex items-center justify-center gap-3 mb-6">
                                <Package className="text-yellow-500" size={28} />
                                <h2 className="text-2xl sm:text-3xl font-bold text-white">Choose Your Plan</h2>
                            </div>

                            {apiError ? (
                                <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-8 text-center">
                                    <XCircle className="mx-auto text-red-500 mb-4" size={48} />
                                    <h3 className="text-xl font-bold text-white mb-2">Connection Error</h3>
                                    <p className="text-gray-400 mb-4">{apiError}</p>
                                    <button
                                        onClick={fetchData}
                                        className="px-6 py-2 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition"
                                    >
                                        Retry
                                    </button>
                                </div>
                            ) : packages.length === 0 ? (
                                <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-8 text-center">
                                    <div className="relative w-12 h-12 mx-auto mb-4">
                                        <div className="absolute inset-0 rounded-full border-4 border-yellow-500/20"></div>
                                        <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-yellow-500 animate-spin"></div>
                                    </div>
                                    <p className="text-gray-400">Loading packages...</p>
                                    <p className="text-xs text-gray-600 mt-2">Make sure server is restarted after migration</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    {packages.map((pkg, idx) => (
                                        <div
                                            key={pkg.id}
                                            onClick={() => setSelectedPackage(pkg)}
                                            className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-300 transform hover:-translate-y-2 ${selectedPackage?.id === pkg.id
                                                ? 'ring-2 ring-yellow-500 shadow-xl shadow-yellow-500/20'
                                                : 'hover:shadow-lg'
                                                }`}
                                        >
                                            {/* Gradient Border Effect */}
                                            <div className={`absolute inset-0 bg-gradient-to-br ${idx === 0 ? 'from-blue-500 to-cyan-500' :
                                                idx === 1 ? 'from-yellow-500 to-orange-500' :
                                                    'from-violet-500 to-purple-500'
                                                } opacity-10 group-hover:opacity-20 transition-opacity`}></div>

                                            <div className="relative bg-[#12161c] border border-gray-800 rounded-2xl p-6">
                                                {idx === 1 && (
                                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full text-xs font-bold text-black">
                                                        POPULAR
                                                    </div>
                                                )}

                                                <div className="flex items-center justify-between mb-4 mt-2">
                                                    <h3 className="text-2xl font-bold text-white">{pkg.name}</h3>
                                                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${selectedPackage?.id === pkg.id
                                                        ? 'border-yellow-500 bg-yellow-500'
                                                        : 'border-gray-600'
                                                        }`}>
                                                        {selectedPackage?.id === pkg.id && <CheckCircle size={14} className="text-black" />}
                                                    </div>
                                                </div>

                                                <div className="mb-4">
                                                    <span className="text-4xl font-bold text-white">${pkg.price}</span>
                                                    <span className="text-gray-500 ml-2">/ {pkg.durationDays} days</span>
                                                </div>

                                                <div className="space-y-3 mb-6">
                                                    <div className="flex items-center gap-3 text-sm">
                                                        <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                                                            <TrendingUp size={16} className="text-green-400" />
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-400">Daily Profit</span>
                                                            <span className="text-green-400 font-bold ml-2">{pkg.minDailyProfitPercent}% - {pkg.maxDailyProfitPercent}%</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-sm">
                                                        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                                                            <DollarSign size={16} className="text-blue-400" />
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-400">Max Investment</span>
                                                            <span className="text-white font-bold ml-2">${pkg.maxInvestment}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3 text-sm">
                                                        <div className="w-8 h-8 rounded-lg bg-yellow-500/10 flex items-center justify-center">
                                                            <Zap size={16} className="text-yellow-400" />
                                                        </div>
                                                        <div>
                                                            <span className="text-gray-400">Max Leverage</span>
                                                            <span className="text-yellow-400 font-bold ml-2">{pkg.maxLeverage}x</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <button className={`w-full py-3 rounded-xl font-medium transition-all ${selectedPackage?.id === pkg.id
                                                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                                                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                                                    }`}>
                                                    {selectedPackage?.id === pkg.id ? 'Selected' : 'Select Plan'}
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Configuration Form */}
                        {selectedPackage && (
                            <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6 sm:p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center">
                                        <Settings size={20} className="text-black" />
                                    </div>
                                    <h3 className="text-xl font-bold text-white">Configure Your Bot</h3>
                                </div>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Left Column */}
                                    <div className="space-y-6">
                                        {/* Investment Amount */}
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <label className="text-sm font-medium text-gray-300">Investment Amount (USDT)</label>
                                                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-green-500/10 border border-green-500/30">
                                                    <Wallet size={14} className="text-green-400" />
                                                    <span className="text-xs text-gray-400">Available:</span>
                                                    <span className="text-sm font-bold text-green-400">${walletBalance.toFixed(2)}</span>
                                                </div>
                                            </div>
                                            <div className="relative">
                                                <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
                                                <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    value={investmentAmount === 0 ? '' : investmentAmount}
                                                    onChange={(e) => {
                                                        const val = e.target.value.replace(/[^0-9.]/g, '');
                                                        setInvestmentAmount(val === '' ? 0 : parseFloat(val));
                                                    }}
                                                    placeholder="Enter amount"
                                                    className="w-full bg-[#1a1f26] rounded-xl pl-12 pr-4 py-4 text-white text-lg font-medium border border-gray-700 focus:border-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500/20 transition-all"
                                                />
                                            </div>
                                            <p className="text-xs text-gray-500 mt-2">
                                                Min: ${settings.minInvestment || 50} • Max: ${Math.min(settings.maxInvestment || 5000, selectedPackage.maxInvestment)}
                                            </p>
                                        </div>

                                        {/* Leverage */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-3">Leverage</label>
                                            <div className="flex gap-3">
                                                {[1, 2, 3].filter(l => l <= selectedPackage.maxLeverage).map(l => (
                                                    <button
                                                        key={l}
                                                        onClick={() => setLeverage(l)}
                                                        className={`flex-1 py-4 rounded-xl font-bold text-lg transition-all ${leverage === l
                                                            ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black shadow-lg shadow-yellow-500/20'
                                                            : 'bg-[#1a1f26] text-gray-300 border border-gray-700 hover:border-gray-600'
                                                            }`}
                                                    >
                                                        {l}x
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Position Mode */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-300 mb-3">Position Mode</label>
                                            <div className="grid grid-cols-3 gap-3">
                                                {[
                                                    { mode: 'LONG_ONLY', label: 'Long', icon: ArrowUpCircle, color: 'from-green-500 to-emerald-500' },
                                                    { mode: 'SHORT_ONLY', label: 'Short', icon: ArrowDownCircle, color: 'from-red-500 to-rose-500' },
                                                    { mode: 'BOTH', label: 'Both', icon: Activity, color: 'from-yellow-500 to-orange-500' }
                                                ].map(({ mode, label, icon: Icon, color }) => (
                                                    <button
                                                        key={mode}
                                                        onClick={() => setPositionMode(mode)}
                                                        className={`py-4 rounded-xl font-medium transition-all flex flex-col items-center gap-2 ${positionMode === mode
                                                            ? `bg-gradient-to-r ${color} text-white shadow-lg`
                                                            : 'bg-[#1a1f26] text-gray-400 border border-gray-700 hover:border-gray-600'
                                                            }`}
                                                    >
                                                        <Icon size={20} />
                                                        <span className="text-sm">{label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Right Column */}
                                    <div className="space-y-6">
                                        {/* Crypto Selection */}
                                        <div>
                                            <div className="flex items-center justify-between mb-3">
                                                <label className="text-sm font-medium text-gray-300">Select Cryptos</label>
                                                <span className="text-xs text-yellow-500 font-medium">{selectedCryptos.length}/10 selected</span>
                                            </div>
                                            <div className="bg-[#1a1f26] rounded-xl border border-gray-700 p-3 max-h-48 overflow-y-auto">
                                                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                                                    {cryptos.map(crypto => (
                                                        <button
                                                            key={crypto.symbol}
                                                            onClick={() => toggleCrypto(crypto.symbol)}
                                                            className={`px-3 py-2 rounded-lg text-xs font-bold transition-all ${selectedCryptos.includes(crypto.symbol)
                                                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                                                                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                                                                }`}
                                                        >
                                                            {crypto.symbol.replace('USDT', '')}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Summary */}
                                        <div className="bg-gradient-to-br from-[#1a1f26] to-[#161b22] rounded-xl border border-gray-700 p-5">
                                            <h4 className="text-sm font-medium text-gray-400 mb-4">Order Summary</h4>
                                            <div className="space-y-3">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-400">Subscription Fee</span>
                                                    <span className="text-white font-medium">${selectedPackage.price}</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-gray-400">Investment Amount</span>
                                                    <span className="text-white font-medium">${investmentAmount}</span>
                                                </div>
                                                <div className="border-t border-gray-700 pt-3 mt-3">
                                                    <div className="flex justify-between">
                                                        <span className="text-gray-300 font-medium">Total Required</span>
                                                        <span className={`text-2xl font-bold ${(parseFloat(selectedPackage.price) + investmentAmount) > walletBalance
                                                            ? 'text-red-400'
                                                            : 'bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent'
                                                            }`}>
                                                            ${(parseFloat(selectedPackage.price) + investmentAmount).toFixed(2)}
                                                        </span>
                                                    </div>
                                                    {(parseFloat(selectedPackage.price) + investmentAmount) > walletBalance && (
                                                        <div className="mt-2 flex items-center gap-2 text-red-400 text-sm">
                                                            <AlertTriangle size={16} />
                                                            <span>Insufficient balance! Need ${((parseFloat(selectedPackage.price) + investmentAmount) - walletBalance).toFixed(2)} more USDT</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <button
                                    onClick={handleSubscribe}
                                    disabled={subscribing || selectedCryptos.length === 0 || investmentAmount < (settings.minInvestment || 50) || (parseFloat(selectedPackage.price) + investmentAmount) > walletBalance}
                                    className="w-full mt-6 py-4 rounded-xl bg-gradient-to-r from-yellow-500 via-yellow-400 to-orange-500 text-black font-bold text-lg hover:shadow-xl hover:shadow-yellow-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                                >
                                    {subscribing ? (
                                        <>
                                            <RefreshCw size={22} className="animate-spin" />
                                            Processing...
                                        </>
                                    ) : (parseFloat(selectedPackage.price) + investmentAmount) > walletBalance ? (
                                        <>
                                            <AlertTriangle size={22} />
                                            Insufficient Balance
                                        </>
                                    ) : (
                                        <>
                                            <Zap size={22} />
                                            Activate Futures Bot
                                            <ArrowRight size={22} />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Dashboard for subscribed users */
                    <div className="space-y-6">
                        {/* Tabs */}
                        <div className="flex gap-2 border-b border-gray-800 pb-3 overflow-x-auto">
                            {[
                                { id: 'overview', label: 'Overview', icon: BarChart3 },
                                { id: 'positions', label: 'Positions', icon: Activity },
                                { id: 'trades', label: 'Trade History', icon: History },
                                { id: 'reports', label: 'Daily Reports', icon: Target },
                                { id: 'transactions', label: 'Transactions', icon: Wallet },
                                { id: 'settings', label: 'Settings', icon: Settings }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`flex items-center gap-2 px-5 py-3 rounded-xl whitespace-nowrap transition-all ${activeTab === tab.id
                                        ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-medium shadow-lg shadow-yellow-500/20'
                                        : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
                                        }`}
                                >
                                    <tab.icon size={18} />
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Overview Tab */}
                        {activeTab === 'overview' && (
                            <div className="space-y-6">
                                {/* Stats Grid */}
                                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                    {[
                                        { label: 'Current Balance', value: `$${parseFloat(subscription.currentBalance).toFixed(2)}`, color: 'text-white' },
                                        { label: 'Total Profit', value: `$${parseFloat(subscription.totalProfit).toFixed(2)}`, color: parseFloat(subscription.totalProfit) >= 0 ? 'text-green-400' : 'text-red-400' },
                                        { label: 'Win Rate', value: `${subscription.winRate || 0}%`, color: 'text-yellow-400' },
                                        { label: 'Total Trades', value: subscription.totalTrades, color: 'text-white' }
                                    ].map((stat, i) => (
                                        <div key={i} className="bg-[#12161c] rounded-2xl border border-gray-800 p-5">
                                            <div className="text-sm text-gray-400 mb-2">{stat.label}</div>
                                            <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* Bot Control */}
                                <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-xl font-bold text-white mb-1">Bot Status</h3>
                                            <p className="text-sm text-gray-400">
                                                Leverage: {subscription.leverage}x • Mode: {subscription.positionMode}
                                            </p>
                                        </div>
                                        <div className={`flex items-center gap-2 px-5 py-2.5 rounded-full ${subscription.isBotRunning
                                            ? 'bg-green-500/10 border border-green-500/30'
                                            : 'bg-gray-800 border border-gray-700'
                                            }`}>
                                            <div className={`w-2.5 h-2.5 rounded-full ${subscription.isBotRunning ? 'bg-green-400 animate-pulse' : 'bg-gray-500'}`}></div>
                                            <span className={subscription.isBotRunning ? 'text-green-400 font-medium' : 'text-gray-400'}>
                                                {subscription.isBotRunning ? 'Running' : 'Stopped'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="flex gap-4">
                                        {!subscription.isBotRunning ? (
                                            <button
                                                onClick={handleStartBot}
                                                className="flex-1 flex items-center justify-center gap-3 py-4 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold hover:shadow-lg hover:shadow-green-500/20 transition-all"
                                            >
                                                <Play size={22} />
                                                Start Bot
                                            </button>
                                        ) : (
                                            <button
                                                onClick={handleStopBot}
                                                className="flex-1 flex items-center justify-center gap-3 py-4 rounded-xl bg-gradient-to-r from-red-500 to-rose-500 text-white font-bold hover:shadow-lg hover:shadow-red-500/20 transition-all"
                                            >
                                                <Square size={22} />
                                                Stop Bot
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* Open Positions on Dashboard */}
                                <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-xl font-bold text-white">Open Positions</h3>
                                        {subscription.openPositions?.length > 0 && (
                                            <span className="text-sm text-gray-400">
                                                {subscription.openPositions.length} active
                                            </span>
                                        )}
                                    </div>
                                    {subscription.openPositions?.length > 0 ? (
                                        <div className="overflow-x-auto">
                                            <table className="w-full">
                                                <thead>
                                                    <tr className="text-gray-400 text-sm border-b border-gray-800">
                                                        <th className="text-left pb-3 pr-4 whitespace-nowrap">Pair</th>
                                                        <th className="text-left pb-3 pr-4 whitespace-nowrap">Side</th>
                                                        <th className="text-left pb-3 pr-4 whitespace-nowrap">Strategy</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Entry Price</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Current</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Margin</th>
                                                        <th className="text-right pb-3 pr-4 whitespace-nowrap">Leverage</th>
                                                        <th className="text-right pb-3 whitespace-nowrap">P&L</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {subscription.openPositions.map((pos) => (
                                                        <tr key={pos.id} className="border-b border-gray-800/50">
                                                            <td className="py-3 pr-4 text-white font-medium whitespace-nowrap">
                                                                {pos.symbol}
                                                            </td>
                                                            <td className="py-3 pr-4 whitespace-nowrap">
                                                                <span className={`px-2 py-1 rounded text-xs font-bold ${pos.side === 'LONG' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                                                    {pos.side}
                                                                </span>
                                                            </td>
                                                            <td className="py-3 pr-4 text-gray-400 text-sm whitespace-nowrap">
                                                                {pos.strategy}
                                                            </td>
                                                            <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                                ${parseFloat(pos.entryPrice).toFixed(4)}
                                                            </td>
                                                            <td className="py-3 pr-4 text-right text-gray-300 whitespace-nowrap">
                                                                ${pos.currentPrice?.toFixed(4) || '-'}
                                                            </td>
                                                            <td className="py-3 pr-4 text-right text-white whitespace-nowrap">
                                                                ${parseFloat(pos.marginUsed).toFixed(2)}
                                                            </td>
                                                            <td className="py-3 pr-4 text-right text-yellow-400 font-medium whitespace-nowrap">
                                                                {pos.leverage}x
                                                            </td>
                                                            <td className={`py-3 text-right font-bold whitespace-nowrap ${parseFloat(pos.unrealizedPnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                                {parseFloat(pos.unrealizedPnl) >= 0 ? '+' : ''}${parseFloat(pos.unrealizedPnl).toFixed(4)}
                                                                <span className="text-xs ml-1 opacity-80">
                                                                    ({parseFloat(pos.unrealizedPnlPercent) >= 0 ? '+' : ''}{parseFloat(pos.unrealizedPnlPercent).toFixed(2)}%)
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 text-gray-500">
                                            <Activity size={36} className="mx-auto mb-3 opacity-50" />
                                            <p>No open positions</p>
                                            <p className="text-xs mt-1">Bot will open positions when market conditions are favorable</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Positions Tab */}
                        {activeTab === 'positions' && (
                            <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                <h3 className="text-xl font-bold text-white mb-6">Open Positions</h3>
                                {subscription.openPositions?.length > 0 ? (
                                    <div className="space-y-4">
                                        {subscription.openPositions.map(pos => (
                                            <div key={pos.id} className="bg-gray-800/50 rounded-xl p-5">
                                                <div className="flex items-center justify-between mb-4">
                                                    <div className="flex items-center gap-3">
                                                        <span className={`px-3 py-1.5 rounded-lg text-xs font-bold ${pos.side === 'LONG' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                                            }`}>
                                                            {pos.side} {pos.leverage}x
                                                        </span>
                                                        <span className="text-white font-bold text-lg">{pos.symbol}</span>
                                                    </div>
                                                    <div className={`text-xl font-bold ${parseFloat(pos.unrealizedPnl) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                        {parseFloat(pos.unrealizedPnl) >= 0 ? '+' : ''}{pos.unrealizedPnl}
                                                    </div>
                                                </div>
                                                <div className="grid grid-cols-4 gap-4 text-sm">
                                                    <div>
                                                        <span className="text-gray-500">Entry</span>
                                                        <div className="text-white font-medium">${parseFloat(pos.entryPrice).toFixed(4)}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Current</span>
                                                        <div className="text-white font-medium">${pos.currentPrice?.toFixed(4)}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Margin</span>
                                                        <div className="text-white font-medium">${parseFloat(pos.marginUsed).toFixed(2)}</div>
                                                    </div>
                                                    <div>
                                                        <span className="text-gray-500">Liq. Price</span>
                                                        <div className="text-red-400 font-medium">${parseFloat(pos.liquidationPrice).toFixed(2)}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">
                                        <Activity size={48} className="mx-auto mb-4 opacity-50" />
                                        <p>No open positions</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Trades Tab */}
                        {activeTab === 'trades' && (
                            <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                <h3 className="text-xl font-bold text-white mb-6">Trade History</h3>
                                {trades.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="text-left text-gray-400 text-sm border-b border-gray-800">
                                                    <th className="pb-4">Symbol</th>
                                                    <th className="pb-4">Side</th>
                                                    <th className="pb-4">Entry</th>
                                                    <th className="pb-4">Exit</th>
                                                    <th className="pb-4">Leverage</th>
                                                    <th className="pb-4 text-right">PnL</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {trades.map(trade => (
                                                    <tr key={trade.id} className="border-b border-gray-800/50">
                                                        <td className="py-4 text-white font-medium">{trade.symbol}</td>
                                                        <td className="py-4">
                                                            <span className={`px-2 py-1 rounded text-xs font-bold ${trade.side === 'LONG' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                                                                }`}>
                                                                {trade.side}
                                                            </span>
                                                        </td>
                                                        <td className="py-4 text-gray-300">${parseFloat(trade.entryPrice).toFixed(4)}</td>
                                                        <td className="py-4 text-gray-300">
                                                            {trade.exitPrice ? `$${parseFloat(trade.exitPrice).toFixed(4)}` : '-'}
                                                        </td>
                                                        <td className="py-4 text-yellow-400 font-medium">{trade.leverage}x</td>
                                                        <td className={`py-4 text-right font-bold ${parseFloat(trade.userProfit) >= 0 ? 'text-green-400' : 'text-red-400'
                                                            }`}>
                                                            ${parseFloat(trade.userProfit).toFixed(4)}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">
                                        <History size={48} className="mx-auto mb-4 opacity-50" />
                                        <p>No trades yet</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Reports Tab */}
                        {activeTab === 'reports' && (
                            <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                <h3 className="text-xl font-bold text-white mb-6">Daily Reports</h3>
                                {reports.length > 0 ? (
                                    <div className="space-y-3">
                                        {reports.map(report => (
                                            <div key={report.settlementDate} className="bg-gray-800/50 rounded-xl p-5 flex justify-between items-center">
                                                <div>
                                                    <div className="text-white font-medium">{report.settlementDate}</div>
                                                    <div className="text-sm text-gray-400">
                                                        {report.tradesCount} trades • {report.winningTrades} wins
                                                    </div>
                                                </div>
                                                <div className={`text-xl font-bold ${parseFloat(report.userNetProfit) >= 0 ? 'text-green-400' : 'text-red-400'
                                                    }`}>
                                                    ${parseFloat(report.userNetProfit).toFixed(4)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">
                                        <Target size={48} className="mx-auto mb-4 opacity-50" />
                                        <p>No reports yet</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Transactions Tab */}
                        {activeTab === 'transactions' && (
                            <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                <h3 className="text-xl font-bold text-white mb-6">Fund Transactions</h3>
                                {transactions.length > 0 ? (
                                    <div className="space-y-3">
                                        {transactions.map(tx => (
                                            <div key={tx.id} className="bg-gray-800/50 rounded-xl p-5 flex justify-between items-center">
                                                <div>
                                                    <div className="text-white font-medium">{tx.description}</div>
                                                    <div className="text-sm text-gray-400">
                                                        {new Date(tx.createdAt).toLocaleString()}
                                                    </div>
                                                </div>
                                                <div className={`text-xl font-bold ${tx.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                                    {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(4)} {tx.token}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-12 text-gray-400">
                                        <Wallet size={48} className="mx-auto mb-4 opacity-50" />
                                        <p>No transactions yet</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Settings Tab */}
                        {activeTab === 'settings' && (
                            <div className="space-y-6">
                                {/* Manage Funds Section */}
                                <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                    <h3 className="text-xl font-bold text-white mb-4">Manage Funds</h3>
                                    <p className="text-gray-400 text-sm mb-4">
                                        Current Bot Balance: <span className="text-yellow-500 font-bold text-lg">${parseFloat(subscription.currentBalance).toFixed(2)}</span>
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Add Funds */}
                                        <div className="bg-gray-800/50 rounded-xl p-5">
                                            <label className="text-sm text-gray-400 block mb-3">Add Funds from Spot Wallet</label>
                                            <div className="flex gap-3">
                                                <input
                                                    type="number"
                                                    placeholder="Amount"
                                                    id="futuresAddFundsAmount"
                                                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none"
                                                />
                                                <button
                                                    onClick={async () => {
                                                        const amount = document.getElementById('futuresAddFundsAmount').value;
                                                        if (!amount || parseFloat(amount) <= 0) {
                                                            toast.error('Enter valid amount');
                                                            return;
                                                        }
                                                        try {
                                                            await api.post('/futures-bot/add-funds', { amount: parseFloat(amount) });
                                                            toast.success(`$${amount} added to futures bot`);
                                                            document.getElementById('futuresAddFundsAmount').value = '';
                                                            fetchData();
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.message || 'Failed to add funds');
                                                        }
                                                    }}
                                                    className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-bold hover:shadow-lg hover:shadow-green-500/20 transition-all"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                        {/* Withdraw Funds */}
                                        <div className="bg-gray-800/50 rounded-xl p-5">
                                            <label className="text-sm text-gray-400 block mb-3">Withdraw to Spot Wallet</label>
                                            <div className="flex gap-3">
                                                <input
                                                    type="number"
                                                    placeholder="Amount"
                                                    id="futuresWithdrawFundsAmount"
                                                    className="flex-1 px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:border-yellow-500 outline-none"
                                                />
                                                <button
                                                    onClick={async () => {
                                                        const amount = document.getElementById('futuresWithdrawFundsAmount').value;
                                                        if (!amount || parseFloat(amount) <= 0) {
                                                            toast.error('Enter valid amount');
                                                            return;
                                                        }
                                                        try {
                                                            await api.post('/futures-bot/withdraw-funds', { amount: parseFloat(amount) });
                                                            toast.success(`$${amount} withdrawn to spot wallet`);
                                                            document.getElementById('futuresWithdrawFundsAmount').value = '';
                                                            fetchData();
                                                        } catch (error) {
                                                            toast.error(error.response?.data?.message || 'Failed to withdraw');
                                                        }
                                                    }}
                                                    className="px-6 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-black rounded-lg font-bold hover:shadow-lg hover:shadow-yellow-500/20 transition-all"
                                                >
                                                    Withdraw
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-[#12161c] rounded-2xl border border-gray-800 p-6">
                                    <h3 className="text-xl font-bold text-white mb-6">Subscription Details</h3>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                        <div className="bg-gray-800/50 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm">Package</div>
                                            <div className="text-white font-bold text-lg">{subscription.package?.name}</div>
                                        </div>
                                        <div className="bg-gray-800/50 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm">Investment</div>
                                            <div className="text-white font-bold text-lg">${parseFloat(subscription.investmentAmount).toFixed(2)}</div>
                                        </div>
                                        <div className="bg-gray-800/50 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm">Position Mode</div>
                                            <div className="text-white font-bold text-lg">{subscription.positionMode}</div>
                                        </div>
                                        <div className="bg-gray-800/50 rounded-xl p-4">
                                            <div className="text-gray-400 text-sm">Days Remaining</div>
                                            <div className={`font-bold text-lg ${subscription.isExpiringSoon ? 'text-yellow-400' : 'text-white'}`}>
                                                {subscription.daysRemaining} days
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default FuturesBotTradingPage;
